import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const servicesData = {
  "software-development": {
    name: "Software Development",
    tagline: "Build scalable, efficient, and secure software solutions",
    description: "We design and develop custom software solutions that address your unique business challenges. Our experienced developers leverage modern technologies and best practices to deliver high-quality applications that scale with your business.",
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800",
    benefits: [
      "Custom solutions tailored to your specific needs",
      "Scalable architecture that grows with your business",
      "Modern technology stack and best practices",
      "Agile development with rapid iterations",
      "Comprehensive testing and quality assurance"
    ],
    offerings: [
      {
        title: "Custom Application Development",
        description: "End-to-end development of web, mobile, and enterprise applications built to your specifications."
      },
      {
        title: "API Development & Integration",
        description: "Design and implementation of robust APIs that connect your systems and enable seamless data flow."
      },
      {
        title: "Mobile App Development",
        description: "Native and cross-platform mobile applications for iOS and Android that deliver exceptional user experiences."
      },
      {
        title: "Legacy System Modernization",
        description: "Transform outdated systems into modern, maintainable applications without disrupting your business."
      },
      {
        title: "DevOps & CI/CD",
        description: "Implement automated pipelines and infrastructure to accelerate delivery and improve reliability."
      }
    ],
    technologies: ["React", "Node.js", "Python", "Java", ".NET", "C#", "AWS", "Azure", "Docker", "Kubernetes", "Cassandra", "MySQL", "PostgreSQL", "Oracle", "Aurora", "SQL Server", "Kafka", "ELK Stack"]
  },
  "digital-transformation": {
    name: "Digital Transformation",
    tagline: "Modernize your business with cutting-edge technology",
    description: "Navigate your digital journey with confidence. We help organizations reimagine their business processes, customer experiences, and operational models through strategic technology adoption that delivers measurable results.",
    image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800",
    benefits: [
      "Accelerate time-to-market for new initiatives",
      "Improve operational efficiency and reduce costs",
      "Enhance customer experiences across all touchpoints",
      "Enable data-driven decision making",
      "Build organizational agility and resilience"
    ],
    offerings: [
      {
        title: "Digital Strategy Consulting",
        description: "Develop a comprehensive digital roadmap aligned with your business objectives and market opportunities."
      },
      {
        title: "Business Process Automation",
        description: "Automate manual processes to improve efficiency, reduce errors, and free up resources for higher-value work."
      },
      {
        title: "Customer Experience Transformation",
        description: "Redesign customer journeys and touchpoints to deliver seamless, personalized experiences."
      },
      {
        title: "Change Management",
        description: "Guide your organization through transformation with proven change management methodologies."
      },
      {
        title: "Digital Workplace Solutions",
        description: "Enable modern collaboration and productivity with digital workplace technologies."
      }
    ],
    technologies: ["RPA", "Low-Code Platforms", "Analytics", "AI/ML", "Cloud", "IoT"]
  },
  "managed-services": {
    name: "Managed Services",
    tagline: "Let us handle your technology operations",
    description: "Focus on your core business while we manage your technology operations. Our managed services provide reliable, cost-effective IT management with 24/7 support and proactive monitoring to ensure your systems run smoothly.",
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800",
    benefits: [
      "Reduce operational costs and complexity",
      "24/7 monitoring and support availability",
      "Proactive issue detection and resolution",
      "Access to specialized expertise",
      "Predictable monthly costs"
    ],
    offerings: [
      {
        title: "Infrastructure Management",
        description: "Complete management of your cloud and on-premises infrastructure including servers, networks, and storage."
      },
      {
        title: "Application Support",
        description: "Ongoing support, maintenance, and enhancement of your business applications."
      },
      {
        title: "Security Operations",
        description: "Continuous security monitoring, threat detection, and incident response to protect your assets."
      },
      {
        title: "Performance Monitoring",
        description: "Real-time monitoring and optimization to ensure optimal system performance."
      },
      {
        title: "Help Desk Services",
        description: "Responsive end-user support to keep your workforce productive."
      }
    ],
    technologies: ["ServiceNow", "Splunk", "Datadog", "PagerDuty", "Terraform", "Ansible"]
  },
  "data-engineering": {
    name: "Data Engineering",
    tagline: "Build robust data pipelines and analytics platforms",
    description: "Transform raw data into actionable insights. We build robust data pipelines, warehouses, and analytics platforms that power data-driven decision making and enable advanced analytics and AI initiatives.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800",
    benefits: [
      "Unified view of your business data",
      "Real-time and batch data processing",
      "Scalable data infrastructure",
      "Data quality and governance",
      "Foundation for AI/ML initiatives"
    ],
    offerings: [
      {
        title: "Data Pipeline Development",
        description: "Design and implement automated data pipelines that move, transform, and validate data reliably."
      },
      {
        title: "Data Warehouse Design",
        description: "Build modern data warehouses and data lakes that support analytics at scale."
      },
      {
        title: "ETL/ELT Solutions",
        description: "Implement efficient data integration solutions to consolidate data from multiple sources."
      },
      {
        title: "Real-time Analytics",
        description: "Enable streaming analytics for real-time insights and decision making."
      },
      {
        title: "Data Governance",
        description: "Establish data quality, security, and governance frameworks to ensure data trustworthiness."
      }
    ],
    technologies: ["Snowflake", "Databricks", "Apache Spark", "Airflow", "dbt", "Kafka"]
  },
  "ai-machine-learning": {
    name: "AI & Machine Learning",
    tagline: "Turn data into insights and intelligent automation",
    description: "Unlock the power of artificial intelligence. From predictive analytics to natural language processing, we implement AI solutions that deliver measurable business value and create competitive advantage.",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800",
    benefits: [
      "Automate complex decision-making",
      "Predict customer behavior and trends",
      "Extract insights from unstructured data",
      "Personalize customer experiences",
      "Optimize operations with ML"
    ],
    offerings: [
      {
        title: "Predictive Analytics",
        description: "Build models that forecast trends, identify risks, and predict outcomes to drive better decisions."
      },
      {
        title: "Natural Language Processing",
        description: "Extract insights from text, automate document processing, and build conversational AI."
      },
      {
        title: "Computer Vision",
        description: "Implement image and video analysis for quality control, security, and automation."
      },
      {
        title: "ML Model Development",
        description: "Design, train, and deploy machine learning models tailored to your business problems."
      },
      {
        title: "MLOps",
        description: "Operationalize ML with automated training, deployment, and monitoring pipelines."
      }
    ],
    technologies: ["TensorFlow", "PyTorch", "OpenAI", "AWS SageMaker", "Azure ML", "MLflow"]
  },
  "cloud-services": {
    name: "Cloud Services",
    tagline: "Seamlessly migrate and optimize your IT infrastructure",
    description: "Accelerate your cloud journey with expert guidance. Whether you're migrating, optimizing, or building cloud-native applications, we have the expertise to help you maximize the value of cloud computing.",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800",
    benefits: [
      "Reduce infrastructure costs",
      "Improve scalability and flexibility",
      "Enhance security and compliance",
      "Accelerate innovation",
      "Enable global reach"
    ],
    offerings: [
      {
        title: "Cloud Migration",
        description: "Plan and execute seamless migrations to the cloud with minimal disruption to your business."
      },
      {
        title: "Cloud Architecture Design",
        description: "Design resilient, scalable, and secure cloud architectures that meet your requirements."
      },
      {
        title: "Cost Optimization",
        description: "Identify and implement opportunities to reduce cloud spending without sacrificing performance."
      },
      {
        title: "Multi-Cloud Strategy",
        description: "Develop strategies that leverage multiple cloud providers for optimal results."
      },
      {
        title: "Cloud-Native Development",
        description: "Build applications designed to take full advantage of cloud capabilities."
      }
    ],
    technologies: ["AWS", "Azure", "Google Cloud", "Terraform", "Kubernetes", "Docker"]
  },
  "erp-crm": {
    name: "ERP/CRM",
    tagline: "Streamline your operations with integrated enterprise systems",
    description: "We implement and customize ERP and CRM solutions that align with your business processes. Our expertise spans leading platforms to help you streamline operations and improve customer relationships.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800",
    benefits: [
      "Unified business processes",
      "Improved data visibility",
      "Enhanced customer relationships",
      "Better decision making",
      "Increased operational efficiency"
    ],
    offerings: [
      {
        title: "ERP Implementation",
        description: "End-to-end implementation of ERP systems including planning, configuration, and go-live support."
      },
      {
        title: "CRM Customization",
        description: "Tailor CRM platforms to match your sales, marketing, and service processes."
      },
      {
        title: "System Integration",
        description: "Connect your ERP and CRM systems with other business applications for seamless data flow."
      },
      {
        title: "User Training & Adoption",
        description: "Comprehensive training programs to ensure successful user adoption and value realization."
      },
      {
        title: "Upgrades & Migrations",
        description: "Upgrade to newer versions or migrate between platforms with minimal disruption."
      }
    ],
    technologies: ["SAP", "Oracle", "Salesforce", "Microsoft Dynamics", "NetSuite", "HubSpot"]
  },
  "cpq-enterprise-pricing": {
    name: "CPQ/Enterprise Pricing",
    tagline: "Optimize your pricing and quoting processes",
    description: "Our CPQ solutions help sales teams configure products, generate accurate quotes, and close deals faster. We optimize your pricing strategies to maximize revenue and profitability.",
    image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800",
    benefits: [
      "Faster quote generation",
      "Reduced pricing errors",
      "Improved deal profitability",
      "Better sales productivity",
      "Consistent pricing enforcement"
    ],
    offerings: [
      {
        title: "CPQ Implementation",
        description: "Implement Configure, Price, Quote solutions that streamline your sales process."
      },
      {
        title: "Pricing Optimization",
        description: "Develop data-driven pricing strategies that maximize revenue and margins."
      },
      {
        title: "Quote-to-Cash Automation",
        description: "Automate the entire quote-to-cash process for faster deal closure."
      },
      {
        title: "Revenue Management",
        description: "Implement systems to manage complex revenue recognition and billing scenarios."
      },
      {
        title: "Price Analytics",
        description: "Analyze pricing performance and identify optimization opportunities."
      }
    ],
    technologies: ["Salesforce CPQ", "Vendavo", "PROS", "Apttus", "Oracle CPQ", "SAP CPQ"]
  },
  "site-reliability-engineering": {
    name: "Site Reliability Engineering",
    tagline: "Build and maintain highly reliable systems",
    description: "Our SRE practices ensure your applications are scalable, resilient, and performant in production environments. We bring Google-inspired SRE principles to help you achieve and maintain high system reliability.",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800",
    benefits: [
      "Higher system availability",
      "Faster incident resolution",
      "Improved system performance",
      "Reduced operational toil",
      "Better capacity planning"
    ],
    offerings: [
      {
        title: "Reliability Engineering",
        description: "Establish SLOs, error budgets, and reliability practices that balance innovation with stability."
      },
      {
        title: "Incident Management",
        description: "Implement effective incident response processes and tools to minimize downtime."
      },
      {
        title: "Performance Optimization",
        description: "Identify and resolve performance bottlenecks to ensure optimal system response times."
      },
      {
        title: "Infrastructure as Code",
        description: "Automate infrastructure provisioning and management with modern IaC practices."
      },
      {
        title: "Observability",
        description: "Implement comprehensive monitoring, logging, and tracing for full system visibility."
      }
    ],
    technologies: ["Prometheus", "Grafana", "Terraform", "Kubernetes", "Datadog", "PagerDuty"]
  },
  "quality-assurance-testing": {
    name: "Quality Assurance & Testing",
    tagline: "Deliver software with confidence",
    description: "Our comprehensive QA services ensure your applications meet the highest quality standards before reaching users. We combine automated testing with expert manual testing to catch issues early and often.",
    image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800",
    benefits: [
      "Higher software quality",
      "Faster release cycles",
      "Reduced production defects",
      "Improved user satisfaction",
      "Lower maintenance costs"
    ],
    offerings: [
      {
        title: "Test Automation",
        description: "Design and implement automated testing frameworks that accelerate your testing cycles."
      },
      {
        title: "Performance Testing",
        description: "Validate system performance under load to ensure scalability and reliability."
      },
      {
        title: "Security Testing",
        description: "Identify vulnerabilities and security risks before they can be exploited."
      },
      {
        title: "QA Strategy & Planning",
        description: "Develop comprehensive QA strategies that align testing with business priorities."
      },
      {
        title: "Continuous Testing",
        description: "Integrate testing into your CI/CD pipeline for continuous quality feedback."
      }
    ],
    technologies: ["Selenium", "Cypress", "JMeter", "Postman", "OWASP ZAP", "SonarQube"]
  },
  "dynamic-workforce-solutions": {
    name: "Dynamic Workforce Solutions",
    tagline: "Scale your team with the right talent",
    description: "We provide flexible staffing solutions to help you meet project demands and build high-performing teams. Whether you need individual experts or entire teams, we have the talent to support your initiatives.",
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800",
    benefits: [
      "Access to specialized skills",
      "Flexible scaling options",
      "Reduced hiring time",
      "Lower overhead costs",
      "Risk mitigation"
    ],
    offerings: [
      {
        title: "Staff Augmentation",
        description: "Augment your team with skilled professionals who integrate seamlessly with your organization."
      },
      {
        title: "Dedicated Teams",
        description: "Build dedicated teams focused exclusively on your projects and priorities."
      },
      {
        title: "Resource Planning",
        description: "Strategic workforce planning to ensure you have the right skills at the right time."
      },
      {
        title: "Skill Development",
        description: "Training and upskilling programs to enhance your team's capabilities."
      },
      {
        title: "Managed Capacity",
        description: "Flexible capacity arrangements that scale with your project needs."
      }
    ],
    technologies: ["Agile", "Scrum", "DevOps", "Cloud", "Full-Stack", "Data Science"]
  }
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = servicesData[slug];

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Service Not Found</h1>
          <Link to="/services">
            <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-none">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Services
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="service-detail-page" className="page-transition">
      {/* Hero Section */}
      <section className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <Link 
            to="/services" 
            className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Services
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">
                Our Services
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white font-['Outfit'] mb-6">
                {service.name}
              </h1>
              <p className="text-xl text-slate-300 mb-6 font-['Plus_Jakarta_Sans']">
                {service.tagline}
              </p>
              <p className="text-slate-400 leading-relaxed font-['Plus_Jakarta_Sans']">
                {service.description}
              </p>
            </div>
            <div className="aspect-[4/3] overflow-hidden border border-slate-700">
              <img 
                src={service.image}
                alt={service.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                Why Choose Us
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-['Outfit'] mb-6">
                Key Benefits
              </h2>
              <ul className="space-y-4">
                {service.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-600 font-['Plus_Jakarta_Sans']">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-50 p-8 border border-slate-200">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-4">
                Technologies We Use
              </h3>
              <div className="flex flex-wrap gap-2">
                {service.technologies.map((tech) => (
                  <span 
                    key={tech}
                    className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offerings Section */}
      <section className="bg-slate-50 py-16 lg:py-24 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              What We Offer
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-['Outfit']">
              Our {service.name} Services
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.offerings.map((offering) => (
              <div 
                key={offering.title}
                className="bg-white p-8 border border-slate-200 hover:border-blue-600 transition-colors"
              >
                <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-3">
                  {offering.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed font-['Plus_Jakarta_Sans']">
                  {offering.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-700 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-blue-100 mb-8 font-['Plus_Jakarta_Sans']">
            Let's discuss how our {service.name.toLowerCase()} services can help your business.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-white text-blue-700 hover:bg-slate-100 rounded-none px-8"
            >
              Contact Us
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
